import './gallery-maps.scss';
import 'url:./fonts/**';

const source = 'qagoma';

import * as layers from './map-layers.js';
import style from './map-style.js';
import icons from 'url:./svgs/*.svg';
import artworks from 'bundle-text:./svgs/artworks/*.svg';
import arrowRight from 'bundle-text:/src/assets/svgs/arrow-right.svg';

const iconMap = {
  baby: 'parents_room',
  basket: 'retail',
  coathanger: 'cloakroom',
  film: 'cinema',
  'first-aid': 'first_aid',
  food: 'foodAndBeverage',
  info: 'information_desk',
  person: 'toilet_mens',
  'person-dress': 'toilet_womens',
  ticket: 'cinema_box_office'
};

import geojsonExtent from '@mapbox/geojson-extent';
import maplibregl from 'maplibre-gl';
import PitchToggle from '@watergis/mapbox-gl-pitch-toggle-control';
import BuildingControl from './BuildingControl';

import {
  client as sanity,
  imageUrlBuilder,
  toHTML
} from '/src/utils/sanity/index.js';

import { imageProjection, venuesProjection } from '/src/$content/_common.js';

import {
  eventUrl,
  eventFormatDate,
  withTicketUrl
} from '/src/$content/events.js';

import { getAdmission } from '/src/_includes/_partials/events.js';
import { formatVenues } from '/src/utils/venues.js';
import { formatDays, formatTime } from '/src/utils/dates.js';

import geojson from 'json:/tmp/qagoma.geojson';

class LegendControl {
  constructor() {
    const container = (this.container = document.createElement('div'));
    container.className = 'maplibregl-ctrl maplibregl-ctrl-legend';

    const legend = (this.legend = document.createElement('div'));
    legend.className = 'map__legend';
    legend.innerHTML = `<h2>Legend</h2><ul></ul>`;
    legend.hidden = true;

    this.getContainer = () => this.container;
    this.getLegend = () => this.legend;
  }

  onAdd() {
    var container = this.container,
      legend = this.legend,
      group = document.createElement('div'),
      button = document.createElement('button'),
      span = document.createElement('span');

    group.className = 'maplibregl-ctrl-group';

    button.title = 'Toggle Map Legend';
    button.className = 'maplibregl-ctrl-legend';

    span.className = 'maplibregl-ctrl-icon';

    container.append(group, legend);
    group.append(button);
    button.append(span);

    button.addEventListener('click', () => {
      this.open = !this.open;
      legend.hidden = !this.open;
      button.classList.toggle('active', this.open);
    });

    return container;
  }

  addItem(name, image) {
    const li = document.createElement('li'),
      label = name
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
        .replace(/\b((?:wo)?men|parent)s\b/gi, "$1's")
        .replace(/(Toilet) (.*)/gi, '$2 $1')
        .replace('And', ' and ');

    li.append(image, label);

    this.legend.querySelector('ul').append(li);
  }
}

const mapLegend = new LegendControl();
const buildingControl = new BuildingControl('qagoma');

const mapControls = {
  'top-left': [
    new maplibregl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true
      },
      showUserHeading: true,
      trackUserLocation: true
    }),
    new PitchToggle({}),
    new maplibregl.NavigationControl({
      showZoom: false
    })
  ],
  'bottom-left': [
    new maplibregl.NavigationControl({
      showCompass: false
    }),
    buildingControl,
    mapLegend
  ]
};

const start = (() => {
  const url = new URL(location.href);

  const start = url.searchParams.has('start')
    ? new Date(url.searchParams.get('start'))
    : new Date();

  start.setHours(0, 0, 0, 0);

  return start;
})();

const activeVenues = sanity.fetch(
  `{
  "exhibitionVenues": *[
      _type == "event"
      && type == "exhibition"
      && count(venues) > 0
      && (!defined(end) || end > $start)
      && start <= $start
    ] | order(start desc, priority desc, ticketed desc, string::lower(title) asc, end desc)
    .venues[]._ref
}{
  "venues": *[_type == "venue" && (_id in ^.exhibitionVenues || type == "cinema" || defined(hours) || defined(description)) && !(type in ["external", "galleryBuilding", 'functionSpace'])]
}.venues[]._id`,
  { start }
);

export const init = (Alpine) => {
  Alpine.data('galleryMap', component);
};

export const component = () => ({
  assets: [],
  get selectedFeatures() {
    return Array.from(
      this.assets &&
        new Set(
          this.assets.flatMap(({ _id, venues }) =>
            [_id, venues?.map(({ _id }) => _id)].flat()
          )
        )
    ).filter(Boolean);
  },
  selectedAsset: null,
  selectedVenue: null,
  setAsset: null,
  async init() {
    const cache = new Map();
    const map = mapSetup.bind(this)();

    this.setAsset = (id) => (this.assets = [cache.get(id)]);

    this.$watch('selectedFeatures', (curr, prev) => {
      new Map(
        map
          .querySourceFeatures(source, {
            filter: ['in', ['get', 'venueId'], ['literal', [curr, prev].flat()]]
          })
          .map(({ id, properties: { venueId } }) => [id, venueId])
      ).forEach((venueId, id) => {
        if (!curr.includes(venueId))
          map.removeFeatureState({ id, source }, 'selected');

        if (!prev.includes(venueId))
          map.setFeatureState({ id, source }, { selected: true });
      });
    });

    Alpine.effect(() => {
      if (this.selectedAsset) this.info.open = true;
    });

    Alpine.effect(() => {
      if (!this.info.open) {
        this.assets = [];
        this.selectedVenue = null;
        this.selectedAsset = null;
      }
    });

    Alpine.effect(() => {
      if (this.assets.length > 0) {
        this.info.loading = true;

        const assets = this.assets;

        if (assets.length === 1) {
          const properties = assets[0];

          const {
            _id,
            _type,
            title,
            subtitle,
            image,
            summary,
            introduction,
            description,
            start,
            end,
            venues,
            type,
            hours,
            url
          } = properties;

          this.selectedAsset = _id;

          const when =
              (start || end) &&
              eventFormatDate({ start, end, type }, { showPermanent: true }),
            where =
              _type == 'event' &&
              venues &&
              formatVenues(
                venues.filter(({ type }) => type != 'galleryBuilding'),
                true
              );

          const intro = (summary || introduction || description)?.[0];

          this.info.data = {
            title: [title, subtitle && `<span>${subtitle}</span>`]
              .filter(Boolean)
              .join(' '),
            image: image && imageUrlBuilder(image).width(500).height(238),
            url: url || (_type == 'event' && eventUrl(properties)),
            content: [
              (when || where) && {
                title: [when && 'When', where && 'Where']
                  .filter(Boolean)
                  .join('/'),
                content: toHTML(
                  [when, where].map((x) => ({
                    _type: 'block',
                    style: 'normal',
                    children: [{ _type: 'span', text: x }],
                    listItem: 'bullet'
                  }))
                )
              },
              hours && {
                title: 'Opening Hours',
                content: hours.reduce((html, { days, open, close }) => {
                  let times = open || close;

                  return `${html}<div>
                      ${
                        (days &&
                          `<strong>${formatDays(days)}${
                            times ? `:` : ``
                          }</strong>`) ||
                        ``
                      }
                      ${(times && formatTime(open, close)) || ``}
                    </div>`;
                }, '')
              },
              {
                title: (when || where) && 'About',
                content: toHTML([intro])
              }
            ]
              .filter(Boolean)
              .map(
                ({ title, content }) =>
                  `<div>${
                    title ? `<h2>${title}</h2>` : ``
                  }<div>${content}</div></div>`
              )
              .join('\n\n'),
            admission:
              _type == 'event' && getAdmission(withTicketUrl(properties))
          };
        } else {
          const feature = map
            .querySourceFeatures('qagoma', {
              layers: ['qagomaSpaces'],
              filter: ['==', ['get', 'id'], this.selectedVenue]
            })
            .reduce(
              (acc, { properties }) => Object.assign(acc, properties),
              {}
            );

          this.info.data = {
            title: `${feature.name} <br><span>${assets.length} exhibitions</span>`,
            content: `<ul style="display:grid; gap:var(--g-gap); margin-top:var(--g-gap)">
              ${assets
                .map((properties) => {
                  const { title, subtitle, _id, _type, image, url } =
                    properties;

                  return `<li style="display: flex; display: grid; grid-template-columns: 0.5fr 1fr; gap: var(--g-gap); align-items: end;">
                    <a href="${
                      url || (_type == 'event' && eventUrl(properties))
                    }" @click.prevent="setAsset('${_id}')">
                      <img src="${imageUrlBuilder(image)
                        .width(150)
                        .height(150)
                        .fit('crop')}" style="display:block">
                    </a>
                    <div>
                      <a href="${
                        url || (_type == 'event' && eventUrl(properties))
                      }" @click.prevent="setAsset('${_id}')">
                        <h2 class="cta-see-more" style="margin:0; align-items: end;">
                          <div>
                          ${[title, subtitle && `<span>${subtitle}</span>`]
                            .filter(Boolean)
                            .join(' ')}
                          </div>
                          ${arrowRight}
                        </h2>
                      </a>
                    
                      <div class="card__details">
                        <div class="card__admission">
                          ${getAdmission(withTicketUrl(properties))}
                        </div>
                      </div>
                    </div>
                  </li>`;
                })
                .join('')}</ul>`
          };
        }

        this.info.loading = false;
      }
    });

    Alpine.effect(() => {
      const venueId = this.selectedVenue;

      if (venueId) {
        if (map.hasControl(buildingControl)) {
          let { building, level } = map.querySourceFeatures('qagoma', {
            layers: ['qagomaSpaces'],
            filter: ['==', ['get', 'id'], venueId]
          })[0].properties;

          if (building && level) buildingControl.setLevel(building, level);
        }

        if (venueId === this.selectedAsset) {
          this.info.loading = true;

          return new Promise((resolve) => {
            if (cache.has(venueId)) return resolve(cache.get(venueId));

            return resolve(
              sanity
                .fetch(
                  `*[_id == $id][0]{
                    ...,
                    "assets": *[references($id)][
                      _type == "event"
                      && type == "exhibition"
                      && count(venues) > 0
                      && (!defined(end) || end > $start)
                      && start <= $start
                    ]{
                      ...,
                      "externalEvent": coalesce(externalEvent, ticketing),
                      ticketing,
                      type,
                      _type,
                      start,
                      end,
                      ${venuesProjection},
                      title,
                      subtitle,
                      summary,
                      image,
                      "slug": slug.current
                    } | order(start desc, priority desc, ticketed desc, string::lower(title) asc, end desc),
                    type == "cinema" => {
                      ...,
                      "assets": *[_id == "6ab8b823-f21b-4c06-8228-f7f9b12715e6"]{
                        ...$venueData,
                        ...,
                        "venues": *[_type == "venue" && type == "cinema" && _id != ^._id],
                        "image": coalesce(images[0], [
                          ...*[_type == "venue" && title match "Cinema*" && defined(images)].images[]
                        ][0])
                      }
                    }
                  }{
                    ...$venueData,
                    ...,
                    "image": images[0]
                  }`,
                  {
                    start,
                    id: venueId,
                    venueData: map
                      .querySourceFeatures('qagoma', {
                        layers: ['qagomaSpaces'],
                        filter: [
                          'all',
                          ['has', 'venue'],
                          ['==', ['get', 'venueId'], venueId]
                        ]
                      })
                      .reduce(
                        (acc, { properties }) =>
                          Object.assign(acc, JSON.parse(properties.venue)),
                        {}
                      )
                  }
                )
                .then(({ assets, ...venue }) => {
                  if (assets) {
                    venue.assets = new Set();

                    assets.forEach((asset) => {
                      cache.set(asset._id, asset);
                      venue.assets.add(cache.get(asset._id));
                    });
                  }

                  cache.set(venue._id, venue);

                  return venue;
                })
            );
          })
            .then((venue) => {
              if (venue?.assets?.size > 0) return Array.from(venue.assets);

              return [venue];
            })
            .then((assets) => {
              this.assets = assets;
            });
        }
      }
    });
  },
  info: {
    open: false,
    loading: false,
    data: {
      title: null,
      image: null,
      content: null
    },
    get content() {
      return [this.data.content].flat().filter(Boolean).join('\n\n');
    }
  },
  artwork: {
    ['x-init']() {
      this.building = buildingControl._state.buildings.get(this.building);
    },
    ['x-transition']: undefined,
    ['x-show']: `building.activeLevel && building.activeLevel === level`
  }
});

function mapSetup() {
  geojson.features.forEach(({ properties: { venue, type, id } }, i) => {
    if (venue || type == 'gallery_space')
      geojson.features[i].properties.venueId = venue?._id ?? id;
  });

  const map = new maplibregl.Map({
    container: 'map',
    style: style,
    transformRequest: (url) => ({
      url: new URL(
        `${url}?key=a715f0bf4ea29737`,
        window.location.origin
      ).toString()
    }),
    crossSourceCollisions: false,
    zoom: 16,
    minZoom: 14,
    maxZoom: 20,
    bounds: geojson.properties.extent,
    padding: 20
  }).fitBounds(geojson.properties.extent, { padding: 20, maxZoom: 16 });

  map.on('click', 'qagomaSpaces', onVenueClick.bind(this));
  map.on('click', 'qagomaBaseCover', onBuildingClick);
  map.on('click', 'amenities', onAmenitiesClick);
  map.on('sourcedata', onSourceData);

  map.on('load', () => {
    map.addSource('qagoma', {
      type: 'geojson',
      data: geojson,
      promoteId: 'id'
    });

    addLayers(map);
    loadIcons(map);
    addArtworks(map);
    addControls(map);
    addInterPathwayPopup(map);
    addClosedVenuePopup(map);
  });

  return map;
}

function onSourceData({ dataType, isSourceLoaded, sourceId, target: map }) {
  if (dataType === 'source' && sourceId === source && isSourceLoaded)
    setupFeatures(map);
}

async function loadIcons(map) {
  for (let [id, icon] of Object.entries(icons)) {
    loadImage(icon).then((icon) => {
      id = iconMap?.[id] || id;

      map.addImage(id, icon, {
        pixelRatio: 2.5
      });

      if (!id.match(/arrow|marker|circle|pattern-/)) {
        mapLegend.addItem(id, icon);
      }
    });
  }
}

async function addLayers(map) {
  Object.values(layers).forEach((layer) => map.addLayer(layer));
}

async function setupFeatures(map) {
  activeVenues.then((activeVenues) => {
    map
      .querySourceFeatures(source, {
        filter: ['has', 'venueId']
      })
      .forEach(({ id, properties: { venueId } }) => {
        map.setFeatureState(
          {
            id,
            source
          },
          {
            isSelectable: activeVenues.includes(venueId)
          }
        );
      });
  });
}

async function addControls(map) {
  for (const [location, controls] of Object.entries(mapControls)) {
    for (const control of controls) {
      map.addControl(control, location);
    }
  }
}

async function addArtworks(map) {
  geojson.features
    .filter(({ properties: { type } }) => type == 'artwork')
    .forEach((feature) => addArtwork(map, feature));
}

async function addArtwork(map, { properties, geometry }) {
  const artwork = artworks?.[properties.icon];

  if (artwork) {
    const el = document.createElement('div');

    el.innerHTML = artwork;
    el.className = 'artwork';

    const icon = el.firstElementChild;
    icon.style.opacity = 0;

    if (properties.level) {
      el.setAttribute('x-data', JSON.stringify(properties));
      el.setAttribute('x-bind', `artwork`);
    }

    const marker = new maplibregl.Marker(el, {
      anchor: 'bottom',
      offset: [0, 25]
    })
      .setLngLat(geometry.coordinates)
      .addTo(map);

    map.on('zoom', () => {
      const zoom = map.getZoom();
      const scale = Math.max(0, 1 + (zoom - 19) * 0.3);

      icon.style.opacity = Math.min(Math.max(0, (17 - zoom) / (17 - 18)), 1);
      icon.style.transform = `scale(${scale})`;
      icon.style.transformOrigin = 'center bottom';
    });

    map.on('rotate', () => {
      el.style.zIndex = Math.round(
        (marker._pos.y / map.getContainer().clientHeight) * 100
      );
    });
  }
}

function addInterPathwayPopup(map) {
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    maxWidth: '21em'
  }).setHTML(
    'Our two riverside galleries, Queensland Art Gallery (QAG) and Gallery of Modern Art (GOMA), sit 150 metres apart in the Queensland Cultural Centre'
  );

  map.on('mouseenter', 'qagomaInterBuildingPathwayTarget', (e) => {
    let pathwayFeatures = e.target.queryRenderedFeatures({
      layers: ['qagomaInterBuildingPathway']
    });

    let mappedFeatures = e.features.map((feature) =>
      pathwayFeatures.find(
        ({ geometry }) =>
          JSON.stringify(geometry) == JSON.stringify(feature.geometry)
      )
    );

    if (mappedFeatures.some(({ layer }) => layer.paint['line-opacity']))
      popup.setLngLat(e.lngLat).addTo(map).trackPointer();
  });

  map.on('mouseleave', 'qagomaInterBuildingPathwayTarget', () => {
    if (popup.isOpen()) popup.remove();
  });
}

function addClosedVenuePopup(map) {
  geojson.features
    .filter(
      ({ properties }) =>
        properties.isClosed && properties.type === 'internal_venue'
    )
    .forEach((feature) => {
      console.log(feature);

      const venue = feature.properties.venue,
        venueId = venue._id,
        message = venue.closureMessage;

      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: '21em'
      }).setHTML(toHTML(message));

      map.on('mouseenter', 'qagomaInaccessible', (e) => {
        if (
          e.features.find(({ properties }) => properties.venueId === venueId)
        ) {
          popup.setLngLat(e.lngLat).addTo(map).trackPointer();
        }
      });

      map.on('mouseleave', 'qagomaInaccessible', () => {
        if (popup.isOpen()) popup.remove();
      });
    });
}

function onVenueClick({ target: map, features }) {
  if (map.getZoom() <= 17) return;

  const feature = features.find(({ id }) => id);

  if (feature && feature.state?.isSelectable !== false) {
    this.selectedAsset = this.selectedVenue = feature.properties.venueId;
  }

  return map;
}

function onBuildingClick({ target: map, features }) {
  map.fitBounds(geojsonExtent(features[0]), { padding: 20 });

  return map;
}

function onAmenitiesClick({ target: map, features }) {
  let feature = features[0];

  if (feature) {
    let coordinates = feature.geometry.coordinates.slice(),
      name = feature.properties.name;

    new maplibregl.Popup().setLngLat(coordinates).setHTML(name).addTo(map);
  }

  return map;
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();

    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (err) => reject(err));

    image.src = url;
  });
}

export default init;
